import { useMemo, useRef, useState } from "react"

import { CursorDataContext } from "./CursorDataContext"
import { applyOptions } from "./CursorDataProvider.helpers"
import { autoDetectHotspot } from "../services/autoDetectHotspot"
import { SourceLoader } from "../services/SourceLoader"
import { READ_ERROR, SourceError } from "../services/SourceLoader.errors"

import type { Point } from "../../shared/types"
import type { Frame } from "../types/Frame"
import type { Options } from "../types/Options"
import type { ResolvedResampling } from "../types/Resampling"
import type { Source } from "../types/Source"
import type { ReactNode } from "react"

const ORIGIN: Point = { x: 0, y: 0 }
const DEFAULT_OPTIONS: Options = {
  framing: "fit",
  removeBackground: false,
  trim: false,
  flip: false,
  resampling: "auto",
}

const findAutoHotspot = (outputFrames: Frame[]) =>
  outputFrames.length ? autoDetectHotspot(outputFrames[0].pixels) : ORIGIN

const resolveResampling = (options: Options, source: Source): ResolvedResampling =>
  options.resampling === "auto" ? source.autoResampling : options.resampling

export const CursorDataProvider = ({ children }: { children: ReactNode }) => {
  const [source, setSource] = useState<Source | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [isConverting, setIsConverting] = useState(false)
  const [options, setOptions] = useState<Options>(DEFAULT_OPTIONS)
  // Only the most recent pick may land; a slow decode must not overwrite a newer one.
  const latestFilesRef = useRef<File[] | null>(null)

  // Keyed on the frames rather than the source: the hotspot lives on the source, and moving it
  // must not redo the options on every frame.
  const frames = source?.frames
  const outputResampling = source ? resolveResampling(options, source) : "smooth"
  const outputFrames = useMemo(
    () => (frames ? applyOptions(frames, options, outputResampling) : []),
    [frames, options, outputResampling],
  )
  const autoHotspot = useMemo(() => findAutoHotspot(outputFrames), [outputFrames])

  // A hotspot left on Auto follows the image as the frames or the options change it. With Trim
  // on, any frame can move the shared box, and with it frame 1's Auto point.
  const followAuto = (current: Source, frames: Frame[], next: Options) =>
    current.hotspot.x === autoHotspot.x && current.hotspot.y === autoHotspot.y
      ? findAutoHotspot(applyOptions(frames, next, resolveResampling(next, current)))
      : current.hotspot

  const handleNewSource = async (files: File[]) => {
    if (files.length === 0) return

    latestFilesRef.current = files
    setIsLoading(true)
    try {
      const loaded = await SourceLoader.load(files)
      if (latestFilesRef.current !== files) return

      setSource(loaded)
      setOptions(DEFAULT_OPTIONS)
      setLoadError(null)
    } catch (error) {
      if (latestFilesRef.current !== files) return

      setLoadError(error instanceof SourceError ? error.message : READ_ERROR)
    } finally {
      if (latestFilesRef.current === files) setIsLoading(false)
    }
  }

  const setFrames = (shown: Frame[]) =>
    setSource((current) => {
      if (!current) return current

      const frames = shown.flatMap(({ id }) => current.frames.filter((frame) => frame.id === id))
      return { ...current, frames, hotspot: followAuto(current, frames, options) }
    })

  const addFrames = (added: Frame[]) =>
    setSource((current) => {
      if (!current) return current

      const frames = [...current.frames, ...added]
      return { ...current, frames, hotspot: followAuto(current, frames, options) }
    })

  const setHotspot = (hotspot: Point) => setSource((current) => current && { ...current, hotspot })

  const dismissSuggestion = (option: keyof Options) =>
    setSource(
      (current) =>
        current && { ...current, suggestions: { ...current.suggestions, [option]: undefined } },
    )

  const changeOptions = (next: Options) => {
    setSource(
      (current) => current && { ...current, hotspot: followAuto(current, current.frames, next) },
    )
    setOptions(next)
  }

  return (
    <CursorDataContext
      value={{
        source,
        isLoading,
        loadError,
        isConverting,
        options,
        outputFrames,
        outputResampling,
        autoHotspot,
        handleNewSource,
        setFrames,
        addFrames,
        changeOptions,
        setHotspot,
        dismissSuggestion,
        clearLoadError: () => setLoadError(null),
        setIsConverting,
      }}
    >
      {children}
    </CursorDataContext>
  )
}
